// Libararies
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, DataLoader, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useState,
  useQuery,
  useScrollView,
  useNavigationDOM,
  useDebouncedCallback,
  usePopover,
  useResponsive,
  useTextInput,
  useToast,
  useMemo,
} from '@supermove/hooks';
import {CommunicationKindModel, Conversation, ProjectModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import IconButton from '@shared/design/components/IconButton';
import Line from '@shared/design/components/Line';
import SearchBar from '@shared/design/components/SearchBar';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import CommunicationWidgetTabs from '@shared/modules/Communication/components/CommunicationWidgetTabs';
import CommunicationsWidgetBody from '@shared/modules/Communication/components/CommunicationsWidgetBody';
import CommunicationsWidgetFooter from '@shared/modules/Communication/components/CommunicationsWidgetFooter';
import ConversationForm from '@shared/modules/Communication/forms/ConversationForm';
import useUpdateConversationMutation from '@shared/modules/Communication/hooks/useUpdateConversationMutation';
import WidgetBuilder from '@shared/modules/Project/components/widgets/components/WidgetBuilder';
import useEventListenerForProject from '@shared/modules/SSE/hooks/useEventListenerForProject';
import TextMessageTemplateCategory from '@shared/modules/Sms/enums/TextMessageTemplateCategory';

const ContentContainer = Styled.View`
  flex: 1;
  background-color: ${colors.white};
`;

const SearchBarContainer = Styled.View`
  flex-direction: row;
  padding-left: 16px;
  padding-right: 16px;
  align-items: center;
`;

const useCommunicationCounts = ({project}: {project: ProjectModel}) => {
  return useMemo(() => {
    if (!project.communications) {
      return {
        emailCount: 0,
        textMessageCount: 0,
        callCount: 0,
      };
    }
    return project.communications.reduce(
      (acc, communication) => {
        if (communication.email) {
          acc.emailCount += 1;
        }
        if (communication.textMessage) {
          acc.textMessageCount += 1;
        }
        if (communication.call) {
          acc.callCount += 1;
        }
        return acc;
      },
      {emailCount: 0, textMessageCount: 0, callCount: 0},
    );
  }, [project.communications]);
};

const getCommunications = ({
  project,
  communicationKind,
}: {
  project: ProjectModel;
  communicationKind?: CommunicationKindModel;
}) => {
  if (!communicationKind) {
    return project.communications;
  }
  return project.communications.filter((communication) => {
    if (communicationKind === 'EMAIL') {
      return !!communication.email;
    }
    if (communicationKind === 'SMS') {
      return !!communication.textMessage;
    }
    if (communicationKind === 'CALL') {
      return !!communication.call;
    }
    return false;
  });
};

const ConversationActions = ({project}: {project: ProjectModel}) => {
  const responsive = useResponsive();
  const actionMenu = usePopover();

  const conversation = project.conversations[0];
  const isConversationOpen = conversation.status === Conversation.ConversationStatus.OPEN;

  const successToast = useToast({
    ToastComponent: SuccessToast,
  });

  const {form, handleSubmit, submitting} = useUpdateConversationMutation({
    conversationForm: ConversationForm.edit(conversation),
    onSuccess: () => {
      actionMenu.handleClose();
      successToast.handleToast({
        message: isConversationOpen
          ? `${responsive.desktop ? 'Conversation marked' : 'Marked'} as follow up.`
          : `${responsive.desktop ? 'Conversation marked' : 'Marked'} as resolved.`,
      });
    },
    onError: (error) => console.log({error}),
  });

  const handleToggleStatus = () => {
    form.setFieldValue(
      'conversationForm.status',
      isConversationOpen
        ? Conversation.ConversationStatus.CLOSED
        : Conversation.ConversationStatus.OPEN,
    );
    setTimeout(handleSubmit, 0);
  };

  return (
    <ActionMenuPopover
      popover={actionMenu}
      actions={[
        {
          text: isConversationOpen ? 'Mark as resolved' : 'Mark as follow up',
          onPress: handleToggleStatus,
          isLoading: submitting,
        },
      ]}
    >
      <IconButton source={Icon.EllipsisV} isSecondary onPress={actionMenu.handleToggle} />
    </ActionMenuPopover>
  );
};

const SearchAndActions = ({
  project,
  setSearchQuery,
  isLoading,
  refetch,
}: {
  project: ProjectModel;
  setSearchQuery: (query: string) => void;
  isLoading: boolean;
  refetch: () => void;
}) => {
  const responsive = useResponsive();
  const searchInput = useTextInput();
  const handleChangeQuery = useDebouncedCallback((query) => {
    setSearchQuery(query);
  }, 250);

  useEventListenerForProject({
    projectId: project.id,
    handleEvent: refetch,
    isDisabled: !project.organization.features.isEnabledRealTimeMessages,
  });

  return (
    <SearchBarContainer>
      <SearchBar
        inputRef={searchInput?.ref}
        onChangeText={(text) => handleChangeQuery(text)}
        placeholder={'Search'}
        containerStyle={{flex: 1}}
        style={{flex: 1}}
        defaultValue={''}
        isLoading={isLoading}
        isClearable
        isResponsive
      />
      <Space width={12} />
      {responsive.desktop && <ConversationActions project={project} />}
    </SearchBarContainer>
  );
};

const ProjectConversationsWidgetContent = ({
  project,
  viewer,
  setSearchQuery,
  handleComposeEmail,
  isLoading,
  refetch,
}: {
  project: ProjectModel;
  viewer: UserModel;
  setSearchQuery: (query: string) => void;
  handleComposeEmail: (emailTemplateUuid?: string) => void;
  isLoading: boolean;
  refetch: () => void;
}) => {
  const {params} = useNavigationDOM();
  const conversationsScrollView = useScrollView();
  const [communicationKind, setCommunicationKind] = useState<CommunicationKindModel>();

  useEventListenerForProject({
    projectId: project.id,
    handleEvent: refetch,
    isDisabled: !project.organization.features.isEnabledRealTimeMessages,
  });

  const communicationCounts = useCommunicationCounts({project});
  const {textMessageTemplatesByCategory} = project.organization;

  return (
    <ContentContainer>
      <Space height={16} />
      <SearchAndActions
        project={project}
        setSearchQuery={setSearchQuery}
        isLoading={isLoading}
        refetch={refetch}
      />
      <Space height={8} />
      <CommunicationWidgetTabs
        activeTab={communicationKind}
        handleChangeTab={setCommunicationKind}
        communicationCounts={communicationCounts}
      />
      <Line />
      <CommunicationsWidgetBody
        viewer={viewer}
        conversations={getCommunications({project, communicationKind})}
        conversationsScrollView={conversationsScrollView}
        contactName={project.client.primaryContact.fullName}
      />
      <CommunicationsWidgetFooter
        primaryContact={project.client.primaryContact}
        viewer={viewer}
        refetch={refetch}
        projectId={project.id}
        textMessageTemplates={textMessageTemplatesByCategory}
        selectedTextMessageTemplate={_.find(
          textMessageTemplatesByCategory,
          (template) => template.uuid === params.textMessageTemplateUuid,
        )}
        handleComposeEmail={handleComposeEmail}
      />
    </ContentContainer>
  );
};

const ProjectConversationsWidget = ({
  projectUuid,
  handleComposeEmail,
}: {
  projectUuid: string;
  handleComposeEmail: (emailTemplateUuid?: string) => void;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const {loading, data, refetch} = useQuery<{
    viewer: UserModel;
    project: ProjectModel;
  }>(ProjectConversationsWidget.query, {
    fetchPolicy: 'cache-and-network',
    variables: {
      projectUuid,
      textMessageCategories: [TextMessageTemplateCategory.CUSTOMER],
      searchQuery,
    },
  });

  return (
    <DataLoader loading={loading} data={data} as={WidgetBuilder.Loader}>
      {({loadedData}) => {
        return (
          <ProjectConversationsWidgetContent
            project={loadedData.project}
            viewer={loadedData.viewer}
            setSearchQuery={setSearchQuery}
            handleComposeEmail={handleComposeEmail}
            isLoading={loading}
            refetch={refetch}
          />
        );
      }}
    </DataLoader>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectConversationsWidget.query = gql`
  ${CommunicationsWidgetBody.fragment}
  ${CommunicationsWidgetFooter.fragment}
  ${ConversationForm.edit.fragment}

  query CommunicationsWidget(
    $projectUuid: String!,
    $textMessageCategories: [String],
    $searchQuery: String,
  ) {
    ${gql.query}
    viewer {
        id
        ...CommunicationsWidgetFooter_Viewer
        ...CommunicationsWidgetBody_Viewer
      }
    project(uuid: $projectUuid) {
      id
      uuid
      organization {
        id
        textMessageTemplatesByCategory(categories: $textMessageCategories) {
          id
          uuid
          ...CommunicationsWidgetFooter_TextMessageTemplate
        }
        features {
          isEnabledRealTimeMessages: isEnabled(feature: "REAL_TIME_MESSAGES")
        }
      }
      client {
        id
        uuid
        primaryContact {
          id
          fullName
          ...CommunicationsWidgetFooter_PrimaryContact
        }
      }
      conversations {
        id
        status
        ...ConversationForm_edit
      }
      communications(searchQuery: $searchQuery) {
        id
        email {
          id
        }
        textMessage {
          id
        }
        call {
          id
        }
        ...CommunicationsWidgetBody_Communication
      }
    }
  }
`;

export default ProjectConversationsWidget;
